import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, User, FileText, ClipboardList, Briefcase, MessageSquare, LogOut, LayoutDashboard, Building, Users, TrendingUp, Sliders, Menu, X, Bookmark, Brain, Compass, Lock, Calendar, FileCheck, Send, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import DemoResetBanner from '../components/DemoResetBanner';
import axios from 'axios';
import { API_BASE } from '../lib/utils';

const AppLayout = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [resetKey, setResetKey] = useState(0);
  const [resumeComplete, setResumeComplete] = useState(false);
  const [personalityComplete, setPersonalityComplete] = useState(false);
  const [careerPrefsComplete, setCareerPrefsComplete] = useState(false);
  const [testingComplete, setTestingComplete] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  useEffect(() => {
    if (user?.role === 'apprentice') {
      axios.get(`${API_BASE}/resume/${user.id}`)
        .then(res => setResumeComplete(res.data?.is_complete === true))
        .catch(() => {});
      axios.get(`${API_BASE}/personality-profile/status/${user.id}`)
        .then(res => setPersonalityComplete(res.data?.completed === true))
        .catch(() => {});
      Promise.all([
        axios.get(`${API_BASE}/career-discovery/status/${user.id}`).catch(() => ({ data: { completed: false } })),
        axios.get(`${API_BASE}/preferences/status/${user.id}`).catch(() => ({ data: { completed: false } })),
      ]).then(([dRes, pRes]) => {
        setCareerPrefsComplete(dRes.data?.completed === true && pRes.data?.completed === true);
      });
      axios.get(`${API_BASE}/testing/status/${user.id}`)
        .then(res => setTestingComplete(res.data?.completed === true))
        .catch(() => {});
      axios.get(`${API_BASE}/booking/status/${user.id}`)
        .then(res => setBookingComplete(res.data?.completed === true))
        .catch(() => {});
      axios.get(`${API_BASE}/notifications/${user.id}`)
        .then(res => setUnreadNotifs(res.data?.unread_count || 0))
        .catch(() => {});
    }
  }, [user, resetKey, location.pathname]);

  const handleDemoReset = useCallback(() => {
    setResetKey(k => k + 1);
    setResumeComplete(false);
    setPersonalityComplete(false);
    setCareerPrefsComplete(false);
    setTestingComplete(false);
    setBookingComplete(false);
    navigate('/apprentice/dashboard');
  }, [navigate]);

  const handleQuickDemo = useCallback(() => {
    setResetKey(k => k + 1);
    navigate('/apprentice/assessments');
  }, [navigate]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  const getNavigationItems = () => {
    if (user.role === 'apprentice') {
      return [
        { path: '/apprentice/dashboard', icon: Home, label: 'Dashboard' },
        { path: '/apprentice/profile', icon: User, label: 'Registration' },
        { path: '/apprentice/cv', icon: FileText, label: 'Resume/CV Builder' },
        { path: '/apprentice/personality', icon: Brain, label: 'Personality Profile', locked: !resumeComplete },
        { path: '/apprentice/career-preferences', icon: Compass, label: 'Career Preferences', locked: !personalityComplete },
        { path: '/apprentice/assessments', icon: ClipboardList, label: 'Aptitude Tests', locked: !careerPrefsComplete },
        { path: '/apprentice/booking', icon: Calendar, label: 'Counsellor Booking', locked: !testingComplete },
        { path: '/apprentice/final-doc', icon: FileCheck, label: 'Final Documentation', locked: !bookingComplete },
        { path: '/apprentice/apply', icon: Send, label: 'Apply to Employers', locked: !bookingComplete },
        { path: '/apprentice/notifications', icon: Bell, label: `Notifications${unreadNotifs > 0 ? ` (${unreadNotifs})` : ''}`, locked: false },
        { path: '/apprentice/saved-roles', icon: Bookmark, label: 'Saved Roles', locked: !resumeComplete },
        { path: '/apprentice/matches', icon: Briefcase, label: 'Matches', locked: !resumeComplete },
        { path: '/apprentice/messages', icon: MessageSquare, label: 'Messages', locked: !resumeComplete },
      ];
    } else if (user.role === 'employer') {
      return [
        { path: '/employer/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/employer/candidates', icon: Users, label: 'Candidates' },
        { path: '/employer/post-role', icon: Briefcase, label: 'Post Role' },
        { path: '/employer/pipeline', icon: TrendingUp, label: 'Pipeline' },
        { path: '/employer/messages', icon: MessageSquare, label: 'Messages' },
        { path: '/employer/billing', icon: Building, label: 'Billing' },
      ];
    } else if (user.role === 'admin') {
      return [
        { path: '/admin/overview', icon: LayoutDashboard, label: 'Overview' },
        { path: '/admin/users', icon: Users, label: 'Users' },
        { path: '/admin/matching-weights', icon: Sliders, label: 'Matching' },
        { path: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
      ];
    }
    return [];
  };

  const navItems = getNavigationItems();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo area — with padding-left to clear mobile menu button */}
      <div className="p-4 sm:p-6 flex-shrink-0">
        <div className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-[#d2d2d7] pl-10 lg:pl-0">
          <span className="text-sm sm:text-lg font-semibold text-[#1d1d1f]">ApprenticeshipMatch</span>
        </div>
      </div>

      {/* Scrollable nav */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
        <nav className="space-y-1">
          {navItems.map((item) => {
            if (item.locked) {
              return (
                <div
                  key={item.path}
                  className="flex items-center space-x-3 px-3 sm:px-4 py-2.5 rounded-xl text-sm text-[#b0b0b5] cursor-not-allowed select-none"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0 opacity-40" />
                  <span className="font-normal truncate opacity-50">{item.label}</span>
                  <Lock className="w-3.5 h-3.5 ml-auto flex-shrink-0 opacity-40" />
                </div>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 sm:px-4 py-2.5 rounded-xl text-sm transition-colors ${
                    isActive
                      ? 'bg-[#0071e3] text-white'
                      : 'text-[#1d1d1f] hover:bg-[#f5f5f7]'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-normal truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* User footer — fixed at bottom */}
      <div className="flex-shrink-0 p-4 sm:p-6 border-t border-[#d2d2d7] bg-white">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#0071e3] to-[#00a1ff] rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {user.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold text-[#1d1d1f] truncate">{user.name}</p>
            <p className="text-xs text-[#86868b] capitalize">{user.role}</p>
          </div>
        </div>
        <Button
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-[#1d1d1f] hover:bg-[#f5f5f7] rounded-xl h-9 sm:h-10 text-sm"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-[#d2d2d7]"
        aria-label="Toggle sidebar"
      >
        {sidebarOpen ? <X className="w-5 h-5 text-[#1d1d1f]" /> : <Menu className="w-5 h-5 text-[#1d1d1f]" />}
      </button>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: Fixed, Mobile: Drawer */}
      <aside className={`
        fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#d2d2d7] z-40
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-0">
        <DemoResetBanner onReset={handleDemoReset} onQuickDemo={handleQuickDemo} />
        <Outlet context={{ resetKey }} />
      </main>
    </div>
  );
};

export default AppLayout;
